// src/components/expenses/ExpensesTable.tsx
import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { ExpenseData, ColumnKey } from '../../pages/Expenses'; // Adjust path

interface ExpensesTableProps {
  paginatedData: ExpenseData[];
  visibleColumns: ColumnKey[];
  getColumnDisplayName: (col: ColumnKey) => string;
  getSortIndicator: (key: ColumnKey) => string;
  handleSort: (key: ColumnKey) => void;
  columnFilters: Partial<Record<ColumnKey, string>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<Partial<Record<ColumnKey, string>>>>;
  selectedRows: string[];
  handleSelectAll: () => void;
  handleRowSelect: (id: string) => void;
  handleEdit: (expense: ExpenseData) => void;
  handleDelete: (id: string) => void;
  formatCurrency: (value: string | number) => string;
  getLabel: (group: string, value: string) => string;
  lookupGroupMap: Record<string, string>;
}

const ExpensesTable: React.FC<ExpensesTableProps> = (props) => {
  const {
    paginatedData, visibleColumns, getColumnDisplayName, getSortIndicator, handleSort,
    columnFilters, setColumnFilters, selectedRows, handleSelectAll, handleRowSelect,
    handleEdit, handleDelete, formatCurrency, getLabel, lookupGroupMap
  } = props;

  return (
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200 min-w-max">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
              <input type="checkbox" checked={selectedRows.length === paginatedData.length && paginatedData.length > 0} onChange={handleSelectAll} className="rounded text-amber-600 focus:ring-amber-600" />
            </th>
            {visibleColumns.map(col => (
              <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center justify-between" onClick={() => handleSort(col)}>
                  <span className="mr-2">{getColumnDisplayName(col)}</span>
                  <span className="text-xs text-gray-400">{getSortIndicator(col)}</span>
                </div>
                <input
                  type="text"
                  placeholder={`Filter ${getColumnDisplayName(col)}`}
                  value={columnFilters[col] || ''}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setColumnFilters(prev => ({ ...prev, [col]: e.target.value }))}
                  className="mt-1 w-full p-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-amber-600"
                />
              </th>
            ))}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedData.map((expense: ExpenseData) => (
            <tr key={expense.id} className={`hover:bg-gray-50 transition-colors ${selectedRows.includes(expense.id) ? 'bg-amber-50' : ''}`}>
              <td className="px-6 py-4 whitespace-nowrap">
                <input type="checkbox" checked={selectedRows.includes(expense.id)} onChange={() => handleRowSelect(expense.id)} className="rounded text-amber-600 focus:ring-amber-600" />
              </td>
              {visibleColumns.map(col => (
                <td key={col} className="px-6 py-4 whitespace-nowrap text-sm">
                  {lookupGroupMap[col]
                    ? getLabel(lookupGroupMap[col], expense[col as keyof ExpenseData] as string)
                    : ['expenseAmount', 'totalExpenseAmount'].includes(col)
                    ? <span className="text-amber-600 font-medium">{formatCurrency(expense[col as keyof ExpenseData] as string || '0')}</span>
                    : (expense[col as keyof ExpenseData] as string) || 'N/A'
                  }
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button onClick={() => handleEdit(expense)} className="text-amber-600 hover:text-amber-800 p-1 rounded-md transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(expense.id)} className="text-red-600 hover:text-red-800 p-1 rounded-md transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExpensesTable;